import React from 'react';
import i18n from '../../../i18n';
import { MetricFormatTypeId } from './api/types-graphql';
import {
  CONFIDENTIAL_DISPLAY_KEY,
  CONFIDENTIAL_VALUE,
  DATA_NOT_AVAILABLE_VALUE,
  DEFAULT_CURRENCY,
  UNDEFINED_DISPLAY_KEY,
} from './constants';
import {
  CurrencyBucket,
  CURRENCY_FORMAT_TYPE,
  FormatTypes,
  MetricFormatOptions,
} from './types';
import {
  currencySymbol,
  getFormattingPrecisionForNumber,
  isValidNumber,
  ratio,
  roundOffNumber,
  toFixedWithPrecision,
} from './utils';

export const transformToTranslate = (transform: {
  x: number;
  y: number;
  rotation: number;
}): string => {
  return `translate(${transform.x}px, ${transform.y}px) rotate(${transform.rotation}deg)`;
};

// Ideally, we should use bigdecimal for money related variables to avoid losing precision and buffer overflows
export const formatCurrency = (
  value: string | number | undefined,
  currency: string = DEFAULT_CURRENCY,
  formatType: CURRENCY_FORMAT_TYPE = FormatTypes.CURRENCY_AGGREGATE
) => {
  if (value === CONFIDENTIAL_VALUE) {
    return i18n.t(CONFIDENTIAL_DISPLAY_KEY);
  }
  if (!value) {
    value = '0';
  }

  const valueAsNum = parseFloat(String(value).replace(/\s/g, ''));
  if (isNaN(valueAsNum)) {
    return UNDEFINED_DISPLAY_KEY;
  } else {
    if (currency === 'JPY') {
      const formattedValue = formatJapaneseCurrency(
        Math.abs(valueAsNum),
        formatType
      );
      return `${valueAsNum < 0 ? '-' : ''}${formattedValue}${currencySymbol[currency]}`;
    } else {
      const formattedValue = formatOtherCurrency(
        Math.abs(valueAsNum),
        formatType
      );
      return `${valueAsNum < 0 ? '-' : ''}${currencySymbol[currency]}${formattedValue}`;
    }
  }
};

const formatOtherCurrency = (
  value: number,
  formatType: CURRENCY_FORMAT_TYPE
) => {
  const buckets: CurrencyBucket[] = [
    new CurrencyBucket(Math.pow(10, 12), 'T'),
    new CurrencyBucket(Math.pow(10, 9), 'B'),
    new CurrencyBucket(Math.pow(10, 6), 'M'),
  ];
  return formatCurrencyByBuckets(value, buckets, formatType);
};

const formatCurrencyByBuckets = (
  value: number,
  buckets: CurrencyBucket[],
  formatType: CURRENCY_FORMAT_TYPE
) => {
  const bucketToApply = buckets.find((b) => value >= b.value);
  const numPart = bucketToApply ? value / bucketToApply.value : value;
  const formattedNumPart = formatNumber(numPart, formatType);
  const bucketSymbol = bucketToApply ? bucketToApply.delimiter : '';
  return `${formattedNumPart}${bucketSymbol}`;
};

export const commaSeparate = (num: number | string) =>
  num !== undefined
    ? num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
    : '';
const formatToMillion = (value: string | number) => {
  const roundedToMillionValue = `${toFixedWithPrecision(Number(value) / 1000000, 1)}M`;
  return roundedToMillionValue;
};

export const formatNumber = (
  val: number | string,
  formatType: FormatTypes
): string => {
  if (val === CONFIDENTIAL_VALUE) {
    return i18n.t(CONFIDENTIAL_DISPLAY_KEY);
  }
  if (isValidNumber(val)) {
    const n = Number(val);
    if (formatType === FormatTypes.EXACT_NUMBER) {
      return commaSeparate(n);
    } else {
      if (val === 0) {
        return '0';
      } else {
        const roundedNum = roundOffNumber(val, formatType);
        // After rounding, we might get the same number of digits as the original number
        // or different(for eg: 99.98 would give 100. Therefore, now we need to fetch the precision
        // again for the new number and format it with the correct number of decimal points)
        const precision = getFormattingPrecisionForNumber(
          roundedNum,
          formatType
        );
        const roundedNumWithCorrectDecimals =
          precision !== null ? toFixedWithPrecision(n, precision) : String(n);
        return n >= 1000000
          ? formatToMillion(roundedNumWithCorrectDecimals)
          : commaSeparate(roundedNumWithCorrectDecimals);
      }
    }
  }
  return String(val);
};

const formatJapaneseCurrency = (
  value: number,
  formatType: CURRENCY_FORMAT_TYPE
) => {
  const buckets: CurrencyBucket[] = [
    new CurrencyBucket(Math.pow(10, 16), '京'),
    new CurrencyBucket(Math.pow(10, 12), '兆'),
    new CurrencyBucket(Math.pow(10, 8), '億'),
    new CurrencyBucket(Math.pow(10, 4), '万'),
  ];
  return formatCurrencyByBuckets(value, buckets, formatType);
};

export function formatPercentageNumber(
  num: number | string,
  formatType: FormatTypes = FormatTypes.PERCENTAGE
): string {
  // Don't append % if the value is a string
  if (typeof num === 'string') {
    return num;
  }
  return `${formatNumber(num, formatType)}%`;
}

/** Average calendar year length, accounting for leap years. */
export const TENURE_YEAR_IN_DAYS = 365.25;
/** Average calendar month length (TENURE_YEAR_IN_DAYS / 12). */
export const TENURE_MONTH_IN_DAYS = 30.44;

export const convertNumDaysToYearAndMonth = (days: number) => {
  const sign = days < 0 ? '-' : '';
  const absoluteDays = Math.abs(days);

  const years = Math.floor(ratio(absoluteDays, TENURE_YEAR_IN_DAYS));
  const months = Math.floor(
    ratio(absoluteDays % TENURE_YEAR_IN_DAYS, TENURE_MONTH_IN_DAYS)
  );
  return `${sign}${i18n.t('common:commonValues.misc.durationInYearsAndMonths', {
    years,
    months,
  })}`;
};

// Converts decimal hours (e.g. 7.5) to an HH:MM display string (e.g. "7:30").
// Rounds to the nearest minute and does not roll values >= 24h over into days.
export const convertDecimalHoursToHHMM = (decimalHours: number): string => {
  const totalMinutes = Math.round(Math.abs(decimalHours) * 60);
  // Derive the sign from the rounded result so tiny negative inputs that round
  // to zero render as "0:00" rather than "-0:00".
  const sign = totalMinutes > 0 && decimalHours < 0 ? '-' : '';
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${sign}${hours}:${String(minutes).padStart(2, '0')}`;
};

export const metricFormatTypeToFormatterMap: Record<
  MetricFormatTypeId,
  (val: number | string, options?: MetricFormatOptions) => string
> = {
  [MetricFormatTypeId.Pmft001Salary]: (val, options) => {
    return formatCurrency(val, options?.currency ?? DEFAULT_CURRENCY);
  },
  [MetricFormatTypeId.Pmft002Percentage]: (val) => {
    return formatPercentageNumber(val);
  },
  [MetricFormatTypeId.Pmft004DaysToYearMonth]: (val) => {
    return convertNumDaysToYearAndMonth(Number(val));
  },
  [MetricFormatTypeId.Pmft005Count]: (val) => {
    return formatNumber(val, FormatTypes.COUNT);
  },
  [MetricFormatTypeId.Pmft006Number]: (val) => {
    return formatNumber(val, FormatTypes.NUMBER);
  },
  [MetricFormatTypeId.Pmft007SensitiveNumber]: (val) => {
    return formatNumber(val, FormatTypes.SENSITIVE_NUMBER);
  },
  [MetricFormatTypeId.Pmft008Multiple]: (val) => {
    // Don't append 'x' if the value is confidential
    if (val === CONFIDENTIAL_VALUE) {
      return formatNumber(val, FormatTypes.NUMBER);
    }
    return `${formatNumber(val, FormatTypes.NUMBER)}x`;
  },
  [MetricFormatTypeId.Pmft009Leaves]: (val) => {
    return formatNumber(val, FormatTypes.LEAVES_TAKEN);
  },
  [MetricFormatTypeId.Pmft010Hours]: (val) => {
    return formatNumber(val, FormatTypes.HOURS);
  },
  [MetricFormatTypeId.Pmft011Fte]: (val) => {
    return formatNumber(val, FormatTypes.FTE);
  },
  [MetricFormatTypeId.Pmft012FloatingNumber1]: (val) => {
    return formatNumber(val, FormatTypes.FLOATING_NUMBER_1);
  },
  [MetricFormatTypeId.Pmft013FloatingNumber2]: (val) => {
    return formatNumber(val, FormatTypes.FLOATING_NUMBER_2);
  },
  [MetricFormatTypeId.Pmft014FloatingPercentage1]: (val) => {
    return formatPercentageNumber(val, FormatTypes.FLOATING_PERCENTAGE_1);
  },
  [MetricFormatTypeId.Pmft015FloatingPercentage2]: (val) => {
    return formatPercentageNumber(val, FormatTypes.FLOATING_PERCENTAGE_2);
  },
};

export const formatMetricValue = (
  val: number | string | null,
  formatType: MetricFormatTypeId = MetricFormatTypeId.Pmft005Count,
  options?: MetricFormatOptions
): string => {
  if (val === null) {
    return DATA_NOT_AVAILABLE_VALUE;
  }
  return metricFormatTypeToFormatterMap[formatType](val, options);
};

export type ClickEvent<ElementType = HTMLInputElement> =
  React.MouseEvent<ElementType>;
